import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import { useSelector } from 'react-redux';
import * as Yup from 'yup';

import FmcForm from 'components/FmcForm';
import MagicLinkOtpVerificationView from './MagicLinkOtpVerificationView';
/* TODO: uncomment when resendSms has been implemented for magic link feature
import useOTPSender, { OTPSenderError } from 'hooks/requests/otpSender';
*/
import useTimeout from 'hooks/useTimeout';

import { selectAttempts, selectExpireTime } from 'state/otpCode';
import { selectCreditApplication } from 'state/creditApplication';

import useTranslate from 'hooks/useTranslate';
import { selectMagicLink } from 'state/magicLink';
import { isOtpCode } from 'utils/validations';
import useHandleAcceptedMagicLink from '../useHandleAcceptedMagicLink';
import catchOnly from 'utils/catchOnly';
import {
  MagicLinkVerifyOtpError,
  MagicLinkVerifyError,
} from 'hooks/requests/magicLinkVerify';
import useNavigationHelpers from 'hooks/useNavigationHelpers';

export type FormValues = {
  otpCode: string;
};

const initialValues: FormValues = {
  otpCode: '',
};

type PropTypes = RouteComponentProps & {
  parentPath: string;
};

const MagicLinkOtpVerification: React.FC<PropTypes> = ({ parentPath }) => {
  const magicLink = useSelector(selectMagicLink);
  const handleAcceptedMagicLink = useHandleAcceptedMagicLink();
  const { push } = useNavigationHelpers();

  const creditApplication = useSelector(selectCreditApplication);
  const { mobilePhone, zip, ssn } = creditApplication;
  if (!magicLink || !mobilePhone || !zip || !ssn) {
    throw new Error('Mobile phone, zip, or ssn is missing');
  }

  const otpAttempts = useSelector(selectAttempts);
  const otpExpireTime = useSelector(selectExpireTime);

  const t = useTranslate();

  const schema = Yup.object().shape({
    otpCode: Yup.string()
      .label(t('formLabel.otpCode'))
      .concat(isOtpCode)
      .required(),
  });

  const tooManyAttempts = useMemo(() => otpAttempts >= 3, [otpAttempts]);
  const attemptsRemaining = useMemo(() => {
    if (otpAttempts === 0) {
      return null;
    } else if (otpAttempts >= 3) {
      return t('magicLink.otpVerification.outOfAttempts');
    } else {
      return t('magicLink.otpVerification.attemptsLeft', {
        count: 3 - otpAttempts,
      });
    }
  }, [otpAttempts, t]);

  const [setTimeout] = useTimeout();
  const [timedOut, setTimedOut] = useState<boolean>(false);

  useEffect(() => {
    if (otpExpireTime && otpExpireTime > Date.now()) {
      setTimedOut(false);
      setTimeout(() => setTimedOut(true), otpExpireTime - Date.now());
    } else {
      setTimedOut(true);
    }
  }, [otpExpireTime, setTimeout, setTimedOut]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await handleAcceptedMagicLink(
          magicLink,
          mobilePhone,
          ssn,
          zip,
          values.otpCode,
        );
      } catch (e) {
        if (catchOnly(e, MagicLinkVerifyOtpError, MagicLinkVerifyError)) {
          if (e instanceof MagicLinkVerifyOtpError) {
            // No op, the text field has its own error message
          } else {
            push(`${parentPath}/magic-link-invalid-phone`);
          }
        }
      }
    },
    [
      handleAcceptedMagicLink,
      magicLink,
      mobilePhone,
      parentPath,
      push,
      ssn,
      zip,
    ],
  );

  return (
    <FmcForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      {formProps => (
        <MagicLinkOtpVerificationView
          {...formProps}
          mobilePhone={mobilePhone || ''}
          attemptsRemaining={attemptsRemaining}
          tooManyAttempts={tooManyAttempts}
          timedOut={timedOut}
        />
      )}
    </FmcForm>
  );
};

export default MagicLinkOtpVerification;
